import { useState } from "react";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/apiError";
import { PORTFOLIO_THEMES, DEFAULT_THEME } from "@/data/portfolioThemes";
import { Check, Palette } from "lucide-react";

/**
 * Compact horizontal theme picker for a portfolio.
 * Renders as a strip of 5 swatches. Clicking a swatch PATCHes the
 * portfolio's `theme` on the backend. Design decisions:
 *   - We keep the swatches inline (not a modal) so users see the whole
 *     palette at a glance.
 *   - The active theme gets a check overlay + gold ring.
 */
export default function PortfolioThemePicker({ portfolio, onChanged }) {
  const [busy, setBusy] = useState("");
  const active = portfolio.theme || DEFAULT_THEME;

  const apply = async (key) => {
    if (busy || key === active) return;
    setBusy(key);
    try {
      await axios.patch(`${API}/studio/portfolios/${portfolio.id}/theme`, { theme: key });
      toast.success(`Theme: ${PORTFOLIO_THEMES.find((t) => t.key === key)?.name}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e, "Theme change failed."));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-4" data-testid="portfolio-theme-picker">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-3">
        <Palette className="w-3 h-3" /> Theme
      </div>
      <div className="flex flex-wrap gap-2.5">
        {PORTFOLIO_THEMES.map((t) => {
          const isActive = t.key === active;
          const [c1, c2, c3] = t.swatches;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => apply(t.key)}
              disabled={busy === t.key}
              data-testid={`theme-swatch-${t.key}`}
              title={t.name + " — " + t.tagline}
              className={`group relative rounded-xl border transition p-1 ${
                isActive
                  ? "border-[#D4AF37] ring-1 ring-[#D4AF37]/40"
                  : "border-white/10 hover:border-white/25"
              }`}
            >
              <div className="w-24 h-14 rounded-lg overflow-hidden flex">
                <div className="flex-1" style={{ background: c1 }} />
                <div className="flex-1" style={{ background: c2 }} />
                <div className="flex-1" style={{ background: c3 }} />
              </div>
              <div className={`mt-1 text-[10px] leading-tight ${isActive ? "text-[#D4AF37]" : "text-white/60"}`}>
                {t.name}
              </div>
              {isActive && (
                <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-[#D4AF37] text-black flex items-center justify-center">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
